import { formatDate, formatDateTime, formatMoney, useTranslation } from "@roastery/i18n";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@roastery/ui";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { BookmarkIcon, RotateCcwIcon } from "lucide-react";
import { useState } from "react";
import { OrderStatusBadge } from "../components/order-status-badge";
import { SaveTemplateDialog } from "../components/save-template-dialog";
import { usePortalOrder } from "../lib/portal-api";
import { useReorder } from "../lib/use-reorder";
import { usePortalFormatSettings } from "../lib/use-portal-format";

export const Route = createFileRoute("/_authed/orders/$orderId")({
  component: OrderDetailPage,
});

/**
 * One order, in full (US-10.4). This is the address the confirmation e-mail from US-10.3 links to,
 * so it has to answer the question that e-mail raises — "what did I actually order, and where is
 * it" — without a sign-in dance beyond the portal's own.
 *
 * <p>An order belonging to another customer answers exactly as an invented number does: not found.
 */
function OrderDetailPage() {
  const { orderId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const settings = usePortalFormatSettings();
  const order = usePortalOrder(orderId);
  const reorder = useReorder();
  const [saving, setSaving] = useState(false);

  if (order.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="portal-order-loading">
        {t("app.loading")}
      </p>
    );
  }
  if (order.error || !order.data) {
    return (
      <Card data-testid="portal-order-not-found">
        <CardHeader>
          <CardTitle>{t("portal.orders.detail.notFound")}</CardTitle>
        </CardHeader>
        <CardFooter>
          <Button onClick={() => void navigate({ to: "/orders" })} data-testid="portal-back-to-orders">
            {t("portal.orders.detail.back")}
          </Button>
        </CardFooter>
      </Card>
    );
  }

  const data = order.data;
  const lines = data.lines ?? [];
  const parcels = data.shipments ?? [];
  const address = data.shippingAddress;

  return (
    <div className="flex flex-col gap-4" data-testid="portal-order-detail">
      <Link
        to="/orders"
        className="text-sm text-muted-foreground hover:text-foreground"
        data-testid="portal-orders-back"
      >
        ← {t("portal.orders.detail.back")}
      </Link>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3" data-testid="portal-order-number">
            {data.orderNumber}
            <OrderStatusBadge status={data.status} testId="portal-order-status" />
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="portal-order-reorder" onClick={() => reorder(lines)}>
              <RotateCcwIcon aria-hidden />
              {t("portal.orders.reorder")}
            </Button>
            <Button
              variant="outline"
              data-testid="portal-order-save-template"
              onClick={() => setSaving(true)}
            >
              <BookmarkIcon aria-hidden />
              {t("portal.orders.detail.saveAsTemplate")}
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("portal.orders.detail.shipments")}</CardTitle>
            <CardDescription>
              {t("portal.orders.detail.placedAt")}:{" "}
              {data.placedAt ? formatDateTime(data.placedAt, settings) : "—"} ·{" "}
              {t("portal.orders.detail.requestedDelivery")}:{" "}
              {data.requestedDeliveryDate
                ? formatDate(data.requestedDeliveryDate, settings)
                : t("portal.orders.detail.noDeliveryDate")}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            {parcels.length === 0 ? (
              <p className="text-muted-foreground" data-testid="portal-order-no-shipments">
                {t("portal.orders.detail.noShipments")}
              </p>
            ) : (
              parcels.map((parcel, index) => (
                <div
                  key={`${parcel.trackingNumber ?? "parcel"}-${index}`}
                  className="rounded-md border border-border p-3"
                  data-testid={`portal-order-shipment-${index}`}
                >
                  <div className="font-medium">{parcel.carrier ?? "—"}</div>
                  <div className="tabular-nums text-muted-foreground">
                    {t("portal.orders.detail.tracking")}:{" "}
                    <span className="font-mono whitespace-nowrap">{parcel.trackingNumber ?? "—"}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {t("portal.orders.detail.shippedAt")}:{" "}
                    {parcel.shippedAt ? formatDateTime(parcel.shippedAt, settings) : "—"}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("portal.orders.detail.shippingAddress")}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm" data-testid="portal-order-address">
            {address ? (
              <address className="not-italic text-muted-foreground">
                <div className="font-medium text-foreground">{address.recipient}</div>
                <div>{address.line1}</div>
                {address.line2 ? <div>{address.line2}</div> : null}
                <div>
                  {address.postalCode} {address.city}
                </div>
                <div>{address.country}</div>
              </address>
            ) : (
              <p className="text-muted-foreground">{t("common.none")}</p>
            )}
            {data.notes ? (
              <p className="mt-3 text-muted-foreground" data-testid="portal-order-notes">
                <span className="font-medium text-foreground">{t("portal.orders.detail.notes")}: </span>
                {data.notes}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("portal.orders.detail.lines")}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="portal-order-lines">
            <TableHeader>
              <TableRow>
                <TableHead>{t("portal.cart.columns.product")}</TableHead>
                <TableHead>{t("portal.cart.columns.quantity")}</TableHead>
                <TableHead>{t("portal.catalog.columns.price")}</TableHead>
                <TableHead className="text-right">{t("portal.cart.columns.lineTotal")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.variantId} data-testid={`portal-order-line-${line.sku}`}>
                  <TableCell>
                    <div className="font-medium">{line.productName}</div>
                    <div className="text-xs text-muted-foreground">{line.sku}</div>
                  </TableCell>
                  <TableCell className="tabular-nums">{line.quantity}</TableCell>
                  <TableCell className="tabular-nums">
                    {formatMoney(Number(line.unitPriceNet ?? 0), settings)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {formatMoney(Number(line.lineTotalNet ?? 0), settings)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-1 border-t border-border pt-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t("portal.orders.detail.totalNet")}</span>
            <span className="tabular-nums" data-testid="portal-order-total-net">
              {formatMoney(Number(data.totalNet ?? 0), settings)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t("portal.orders.detail.totalGross")}</span>
            <span className="tabular-nums text-base font-semibold" data-testid="portal-order-total-gross">
              {formatMoney(Number(data.totalGross ?? 0), settings)}
            </span>
          </div>
        </CardFooter>
      </Card>

      <SaveTemplateDialog
        open={saving}
        onClose={() => setSaving(false)}
        lines={lines.map((line) => ({ variantId: line.variantId ?? "", quantity: line.quantity ?? 0 }))}
      />
    </div>
  );
}
